import { useLayoutEffect, useRef, useState } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import YearFilterModel from './model';

const DESKTOP_MAX_VISIBLE = 6;
const MOBILE_MAX_VISIBLE = 4;
const GAP = 16;

type YearFilterViewModelProps = {
  years: string[];
  selectedYear: string | null;
};

export default function YearFilterViewModel({ years, selectedYear }: YearFilterViewModelProps) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const breakpointCap = isDesktop ? DESKTOP_MAX_VISIBLE : MOBILE_MAX_VISIBLE;

  const containerRef = useRef<HTMLDivElement>(null);
  const firstChipRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [chipWidth, setChipWidth] = useState(0);
  const [startIndex, setStartIndex] = useState(0);
  const didInitRef = useRef(false);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const measure = () => {
      setContainerWidth(container.clientWidth);
      if (firstChipRef.current) {
        setChipWidth(firstChipRef.current.getBoundingClientRect().width);
      }
    };

    measure();

    let frameId: number | null = null;
    const observer = new ResizeObserver(() => {
      if (frameId !== null) cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(measure);
    });
    observer.observe(container);

    return () => {
      if (frameId !== null) cancelAnimationFrame(frameId);
      observer.disconnect();
    };
  }, [years.length]);

  const fitCount = YearFilterModel.computeFitCount(containerWidth, chipWidth, GAP);
  const maxVisible = Math.max(1, Math.min(years.length, breakpointCap, fitCount));
  const fits = years.length <= maxVisible;
  const selectedIndex = selectedYear ? years.indexOf(selectedYear) : -1;

  useLayoutEffect(() => {
    if (!didInitRef.current) {
      didInitRef.current = true;
      const anchor = selectedIndex >= 0 ? selectedIndex : years.length - 1;
      setStartIndex(YearFilterModel.clampIndex(anchor - maxVisible + 1, years.length, maxVisible));
      return;
    }
    setStartIndex(current => YearFilterModel.ensureVisible(current, selectedIndex, years.length, maxVisible));
  }, [maxVisible, years.length, selectedIndex]);

  const windowWidth = chipWidth > 0 ? maxVisible * chipWidth + (maxVisible - 1) * GAP : undefined;
  const offset = startIndex * (chipWidth + GAP);

  const next = () => {
    setStartIndex(current => YearFilterModel.clampIndex(current + maxVisible, years.length, maxVisible));
  };
  const prev = () => {
    setStartIndex(current => YearFilterModel.clampIndex(current - maxVisible, years.length, maxVisible));
  };

  const canGoPrev = startIndex > 0;
  const canGoNext = startIndex + maxVisible < years.length;

  return { fits, containerRef, firstChipRef, windowWidth, offset, next, prev, canGoPrev, canGoNext };
}
